import { MarkdownContent } from 'elements/visual/MarkdownContent';
import { DetailQuery_moment_header } from 'graphql/query/__generated__/DetailQuery';
import { EColors, EFonts } from 'libs/styles/styling';
import { getSmallerImage } from 'libs/utils/imageUtils/imageUtils';
import lgZoom from 'lightgallery/plugins/zoom';
import LightGallery from 'lightgallery/react';
import { useRef } from 'react';
import styled, { css } from 'styled-components/macro';

interface Props {
    header: Omit<DetailQuery_moment_header, '__typename'>;
    categories?: string[];
    skipTitle?: boolean;
    className?: string;
    children?: React.ReactNode;
}

export const DetailHeader = (props: Props): JSX.Element => {
    const ref = useRef<HTMLImageElement>(null);

    function openLightGallery() {
        if (ref.current) {
            ref.current.click();
        }
    }

    return (
        <S_DetailHeader className={props.className}>
            <LightGallery
                speed={500}
                plugins={[lgZoom]}
                showCloseIcon={true}
                preload={2}
                infiniteZoom={true}
                zoom={true}
            >
                {props.header.imageUrl && <S_Img ref={ref} src={props.header.imageUrl} />}
            </LightGallery>
            {props.header.imageUrl && (
                <S_ImageContainer>
                    <S_Image
                        $url={getSmallerImage(props?.header?.imageUrl, 400)}
                        $imageStyleId={props.header.imageStyleId}
                        $overFirstImage={false}
                        onClick={openLightGallery}
                    />
                    <S_Image
                        $url={getSmallerImage(props?.header?.imageUrl, 600)}
                        $imageStyleId={props.header.imageStyleId}
                        $overFirstImage={true}
                        onClick={openLightGallery}
                    />
                </S_ImageContainer>
            )}
            <S_Content>
                {!props.skipTitle && <S_Title>{props.header.title}</S_Title>}
                {props.header.text && <S_Text text={props.header.text} />}
                {props.categories && (
                    <S_Categories>
                        {props.categories.map((category) => (
                            <S_Category key={category}>{category}</S_Category>
                        ))}
                    </S_Categories>
                )}
                {props.children}
            </S_Content>
        </S_DetailHeader>
    );
};

const S_DetailHeader = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    background: #ffffff;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05);
    flex-shrink: 0;
    z-index: 1;
`;

export const S_Content = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 20px 20px 20px 20px;
    gap: 15px;
    @media only screen and (min-width: 1260px) {
        padding: 20px 35px 20px 35px;
    }
`;

const S_ImageContainer = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
`;

const S_Image = styled.div<{ $url: string; $imageStyleId: string; $overFirstImage: boolean }>`
    width: auto;
    background-image: url(${(props) => props.$url});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    ${(props) => {
        if (props.$imageStyleId === 'stretch') {
            return css`
                width: 100%;
                height: 225px;
                background-size: cover;
                @media only screen and (min-width: 1260px) {
                    height: 300px;
                }
            `;
        } else {
            return css`
                width: 80%;
                height: 125px;
                margin: 20px 0;
                align-self: center;
            `;
        }
    }}

    ${(props) =>
        props.$overFirstImage &&
        css`
            position: absolute;
            top: 0;
        `}
`;

const S_Title = styled.div`
    font-size: 16px;
    line-height: 20px;
    color: ${EColors.ALMOST_BLACK};
    font-family: ${EFonts.Bold};
`;

const S_Text = styled(MarkdownContent)``;

const S_Categories = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
`;

const S_Category = styled.div`
    border-radius: 7px;
    background-color: #e8e9ea;
    padding: 8px 13px;
    font-size: 12px;
`;

const S_Img = styled.img`
    display: none;
`;
